/**
 * Property create/edit — one component for both `new` and `:id/edit`, switched
 * on whether `useParams().id` is present. Field set matches `propertyCreateSchema`
 * (refactor/backend/src/modules/properties/serializers.ts:225-244); create uses
 * `POST properties/`, edit uses `PATCH properties/{id}/` with only changed
 * fields worth sending as a partial payload.
 *
 * `owner`/`status` are not on this form — both are read-only on the backend
 * serializer (owner is forced to the requester, status only moves via
 * approve/reject).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createProperty, getProperty, updateProperty } from '../../api/endpoints/properties';
import { listUsers } from '../../api/endpoints/auth';
import {
  KENYA_COUNTIES,
  PROPERTY_TYPES,
  type PropertyCreateRequest,
  type PropertyType,
} from '../../types/property';
import { fullName } from '../../types/user';
import { LocationPicker } from '../../components/LocationPicker';

const INITIAL_FORM = {
  name: '',
  property_type: 'APARTMENT' as PropertyType,
  address: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'Kenya',
  description: '',
  /** Comma-separated in the form; split into `string[]` by `toPayload`. */
  features: '',
  year_built: '',
  total_units: '1',
  latitude: '',
  longitude: '',
  water_rate: '',
  electricity_rate: '',
  whatsapp_number: '',
  assigned_agent: '',
};

type FormState = typeof INITIAL_FORM;

function toPayload(form: FormState): PropertyCreateRequest {
  return {
    name: form.name,
    property_type: form.property_type,
    address: form.address,
    city: form.city,
    state: form.state,
    zip_code: form.zip_code,
    country: form.country,
    description: form.description,
    features: form.features
      .split(',')
      .map((feature) => feature.trim())
      .filter((feature) => feature !== ''),
    year_built: form.year_built === '' ? null : Number(form.year_built),
    total_units: form.total_units === '' ? undefined : Number(form.total_units),
    latitude: form.latitude === '' ? null : form.latitude,
    longitude: form.longitude === '' ? null : form.longitude,
    water_rate: form.water_rate === '' ? undefined : form.water_rate,
    electricity_rate: form.electricity_rate === '' ? undefined : form.electricity_rate,
    whatsapp_number: form.whatsapp_number,
    assigned_agent: form.assigned_agent === '' ? null : Number(form.assigned_agent),
  };
}

export function PropertyFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const propertyId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => getProperty(propertyId),
    enabled: isEdit && Number.isFinite(propertyId),
  });

  // ?role=AGENT is widened server-side to every agent in the system, not just
  // ones already linked to one of the caller's properties — see the
  // agent-picker carve-out in users/routes.ts and PARITY_NOTES.md. Without
  // that, a landlord creating their FIRST property would see zero candidates.
  const agentsForAssignment = useQuery({
    queryKey: ['users', 'for-assignment', 'AGENT'],
    queryFn: () => listUsers({ page: 1, role: 'AGENT' }),
  });

  useEffect(() => {
    if (existing.data === undefined) return;
    const property = existing.data;
    setForm({
      name: property.name,
      property_type: property.property_type,
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      country: property.country,
      description: property.description,
      features: property.features.join(', '),
      year_built: property.year_built === null ? '' : String(property.year_built),
      total_units: String(property.total_units),
      latitude: property.latitude ?? '',
      longitude: property.longitude ?? '',
      water_rate: property.water_rate,
      electricity_rate: property.electricity_rate,
      whatsapp_number: property.whatsapp_number,
      assigned_agent: property.assigned_agent === null ? '' : String(property.assigned_agent),
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      isEdit ? updateProperty(propertyId, toPayload(form)) : createProperty(toPayload(form)),
    // 'new' and ':id/edit' are both siblings of ':id' under 'properties', so a
    // relative '..' lands on 'properties' from 'new' and on 'properties/:id'
    // from ':id/edit' — the edit case is already at the right target.
    onSuccess: (property) => navigate(isEdit ? '..' : `../${property.id}`, { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit property' : 'Add property'}</h2>

      <form onSubmit={handleSubmit} noValidate className="form__grid">
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <fieldset className="form__group form__group--full">
          <legend>Identity</legend>

          <label htmlFor="property-name">Name</label>
          <input
            id="property-name"
            required
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
          />
          {fieldError('name') !== undefined && <p className="form__field-error">{fieldError('name')}</p>}

          <label htmlFor="property-type">Type</label>
          <select
            id="property-type"
            value={form.property_type}
            onChange={(event) => update('property_type', event.target.value)}
          >
            {PROPERTY_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <label htmlFor="property-description">Description</label>
          <textarea
            id="property-description"
            value={form.description}
            onChange={(event) => update('description', event.target.value)}
          />

          <label htmlFor="property-features">Features (comma separated)</label>
          <input
            id="property-features"
            placeholder="e.g. Borehole, Backup generator, Gym"
            value={form.features}
            onChange={(event) => update('features', event.target.value)}
          />
        </fieldset>

        <fieldset className="form__group">
          <legend>Location</legend>

          <label htmlFor="property-address">Address</label>
          <input
            id="property-address"
            required
            value={form.address}
            onChange={(event) => update('address', event.target.value)}
          />

          <label htmlFor="property-city">City</label>
          <input
            id="property-city"
            required
            value={form.city}
            onChange={(event) => update('city', event.target.value)}
          />

          <label htmlFor="property-state">County</label>
          <select
            id="property-state"
            required
            value={form.state}
            onChange={(event) => update('state', event.target.value)}
          >
            <option value="">— Select a county —</option>
            {KENYA_COUNTIES.map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </select>

          <label htmlFor="property-zip">Postal code (optional)</label>
          <input
            id="property-zip"
            value={form.zip_code}
            onChange={(event) => update('zip_code', event.target.value)}
          />

          <label htmlFor="property-country">Country</label>
          <input
            id="property-country"
            value={form.country}
            onChange={(event) => update('country', event.target.value)}
          />

          <label htmlFor="property-latitude">Latitude</label>
          <input
            id="property-latitude"
            value={form.latitude}
            onChange={(event) => update('latitude', event.target.value)}
          />

          <label htmlFor="property-longitude">Longitude</label>
          <input
            id="property-longitude"
            value={form.longitude}
            onChange={(event) => update('longitude', event.target.value)}
          />

          <LocationPicker
            latitude={form.latitude === '' ? null : Number(form.latitude)}
            longitude={form.longitude === '' ? null : Number(form.longitude)}
            onPick={(latitude, longitude) => {
              setForm((previous) => ({
                ...previous,
                latitude: latitude.toFixed(6),
                longitude: longitude.toFixed(6),
              }));
            }}
          />
        </fieldset>

        <fieldset className="form__group">
          <legend>Rates &amp; contact</legend>

          <label htmlFor="property-year-built">Year built</label>
          <input
            id="property-year-built"
            type="number"
            value={form.year_built}
            onChange={(event) => update('year_built', event.target.value)}
          />

          <label htmlFor="property-total-units">
            Total units{!isEdit && ' (auto-creates that many blank units)'}
          </label>
          <input
            id="property-total-units"
            type="number"
            min="0"
            disabled={isEdit}
            value={form.total_units}
            onChange={(event) => update('total_units', event.target.value)}
          />

          <label htmlFor="property-water-rate">Water rate</label>
          <input
            id="property-water-rate"
            value={form.water_rate}
            onChange={(event) => update('water_rate', event.target.value)}
          />

          <label htmlFor="property-electricity-rate">Electricity rate</label>
          <input
            id="property-electricity-rate"
            value={form.electricity_rate}
            onChange={(event) => update('electricity_rate', event.target.value)}
          />

          <label htmlFor="property-whatsapp">WhatsApp number</label>
          <input
            id="property-whatsapp"
            value={form.whatsapp_number}
            onChange={(event) => update('whatsapp_number', event.target.value)}
          />

          <label htmlFor="property-agent">Assigned agent</label>
          <select
            id="property-agent"
            value={form.assigned_agent}
            onChange={(event) => update('assigned_agent', event.target.value)}
          >
            <option value="">— Unassigned —</option>
            {(agentsForAssignment.data?.results ?? []).map((agent) => (
              <option key={agent.id} value={agent.id}>
                {fullName(agent)}
              </option>
            ))}
          </select>
        </fieldset>

        <div className="form__actions">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </section>
  );
}

export default PropertyFormPage;
